import cn from 'clsx';
import type { IMenuItemSDKAction } from '@wix/editor-elements-corvid-utils';
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import Link from '@wix/thunderbolt-elements/components/Link';
import { testIds } from '../../testIds';
import type {
  IMenuItemLabelProps,
  MenuItemProps,
} from '../../../StylableHorizontalMenu.types';
import { getLabelClasses } from './styles/getLabelClasses';
import { getItemDepthSelector } from '../../../../../common/menu';
import { MegaMenuContextProvider } from '../../../../MegaMenuContainerItem/viewer/MegaMenuContext';
import { st, classes as sharedDepthClasses } from './styles/itemShared.st.css';
import AccessibilityIcon from './AccessibilityIcon';
import { useLatest } from '@wix/thunderbolt-elements/dist/providers/useLatest';

const createAccessibilityIconEventListeners = (
  setIsHovered: (val: React.SetStateAction<boolean>) => void,
  setIsIconShown: (val: React.SetStateAction<boolean>) => void,
) => {
  const hideSubmenu = () => setIsHovered(false);
  const toggleSubmenu = () => setIsHovered(isHovered => !isHovered);
  const hideIcon = () => setIsIconShown(false);
  const showIcon = () => setIsIconShown(true);

  return {
    onFocus: () => {
      showIcon();
      hideSubmenu();
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') {
        toggleSubmenu();
      } else if (e.key === 'Escape') {
        focusOnClosestIcon(e);
      } else if (e.shiftKey && e.key === 'Tab') {
        hideSubmenu();
        hideIcon();
      }
    },
  };
};

const createEventListeners = (
  setIsHovered: (val: React.SetStateAction<boolean>) => void,
  setIsIconShown: (val: React.SetStateAction<boolean>) => void,
) => {
  const showSubmenu = () => setIsHovered(true);
  const hideSubmenu = () => setIsHovered(false);
  const hideIcon = () => setIsIconShown(false);

  return {
    onMouseEnter: showSubmenu,
    onMouseLeave: hideSubmenu,
    onBlur: (e: React.FocusEvent) => {
      if (
        e.relatedTarget &&
        !e.currentTarget.contains(e.relatedTarget) &&
        !e.relatedTarget?.contains(e.currentTarget) // Fixes Mobile Safari blur issue where clicking submenu links closes it, blocking navigation. Details: ECL-8426
      ) {
        hideSubmenu();
        hideIcon();
      }
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        hideSubmenu();
        focusOnClosestIcon(e);
        e.stopPropagation();
      }
    },
  };
};

const focusOnClosestIcon = (e: React.KeyboardEvent) => {
  const { currentTarget } = e;

  const depth = currentTarget.getAttribute('data-item-depth');

  if (depth !== null) {
    const closestDepth = getItemDepthSelector(parseInt(depth, 10) - 1);
    const parentNavigateTo = currentTarget.closest(closestDepth);
    const iconWrapper = (parentNavigateTo?.firstChild as HTMLElement)
      ?.children[1];
    const icon = iconWrapper?.firstChild as HTMLElement;

    icon?.focus();
  }
};

const createSDKAction = (
  item: MenuItemProps,
  selected: boolean,
  sdkAction?: IMenuItemSDKAction,
) =>
  sdkAction &&
  ((e: React.MouseEvent) => {
    sdkAction?.(e, {
      ...item,
      selected,
    });
  });

export const MenuItemLabel: React.FC<IMenuItemLabelProps> = ({
  item,
  className,
  withSubItemsClassname,
  isCurrentItem: isCurrentPage,
  depth,
  isStretched,
  hasColumnSubSubs,
  positionUpdaters: positionUpdatersMap,
  positionBoxRef,
  children,
  onItemClick,
  onItemDblClick,
  onItemMouseIn,
  onItemMouseOut,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isIconShown, setIsIconShown] = useState(false);
  const latestIsIconShown = useLatest(isIconShown);
  const labelRef = useRef<HTMLAnchorElement | HTMLDivElement | null>(null);
  const itemWrapperRef = useRef<HTMLLIElement>(null);

  // CSS pre-hydration sync: if the user is hovering this item when React
  // hydrates (visible via CSS :hover rules), sync to React state so JS
  // takes over seamlessly after data-prehydration is removed.
  useLayoutEffect(() => {
    if (!isHovered && itemWrapperRef.current?.matches(':hover')) {
      setIsHovered(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { label, link, forceHovered = false } = item;
  const eventListeners = createEventListeners(setIsHovered, setIsIconShown);
  const accessibilityIconEventListeners = createAccessibilityIconEventListeners(
    setIsHovered,
    setIsIconShown,
  );

  const isHeading = hasColumnSubSubs && depth === 1;
  const classes = getLabelClasses({
    depth,
    isHovered: isHeading ? false : isHovered,
    isCurrentPage,
    className,
  });

  const positionUpdaters = positionUpdatersMap[depth];
  useEffect(() => {
    if (
      !isHovered ||
      !labelRef.current ||
      !positionBoxRef.current ||
      !positionUpdaters
    ) {
      return;
    }

    const { onEnter, onLeave } = positionUpdaters({
      label: labelRef.current,
      positionBox: positionBoxRef.current,
      isStretched,
    });
    onEnter();
    setIsOpen(true);
    return () => {
      onLeave();
      setIsOpen(false);
    };
  }, [isHovered, isStretched, positionBoxRef, positionUpdaters, item]);

  useEffect(() => {
    setIsHovered(forceHovered);
  }, [forceHovered]);

  useEffect(() => {
    // To handle when switching from tab navigation to mouse
    const hideIconAndCloseMenuOnMouseMove = () => {
      if (latestIsIconShown.current) {
        setIsIconShown(false);
        setIsHovered(false);
      }
    };

    window.addEventListener('mousemove', hideIconAndCloseMenuOnMouseMove);

    return () => {
      window.removeEventListener('mousemove', hideIconAndCloseMenuOnMouseMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasAccessibilityIcon = (item.items?.length || item.slot) && !isHeading;

  return (
    <li
      ref={itemWrapperRef}
      className={cn(classes.itemWrapper, withSubItemsClassname)}
      data-testid={testIds.menuItem(depth)}
      data-item-depth={depth}
      data-is-current={isCurrentPage}
      aria-current={isCurrentPage}
      {...(isHovered && {
        'data-hovered': true,
      })}
      {...(isOpen && {
        'data-shown': true,
      })}
      {...eventListeners}
    >
      <div
        className={st(sharedDepthClasses.rootContainer, {
          isRow: hasAccessibilityIcon,
        })}
      >
        <Link
          {...link}
          className={cn(classes.root, sharedDepthClasses.menuItem)}
          ref={labelRef}
          activateByKey="Enter"
          onClick={createSDKAction(item, isCurrentPage, onItemClick)}
          onMouseEnter={createSDKAction(item, isCurrentPage, onItemMouseIn)}
          onMouseLeave={createSDKAction(item, isCurrentPage, onItemMouseOut)}
          onDoubleClick={createSDKAction(item, isCurrentPage, onItemDblClick)}
          {...(!!children && {
            'aria-haspopup': true,
            'aria-expanded': isHovered,
          })}
          {...(!!children &&
            !link?.href && {
              role: 'button',
            })}
          tabIndex={0}
          data-item-label
        >
          <div className={classes.container}>
            <span className={classes.label}>{label}</span>
          </div>
        </Link>

        {hasAccessibilityIcon && (
          <div
            className={st(sharedDepthClasses.accessibilityIconWrapper, {
              isIconShown,
              isTopLevel: depth === 0,
            })}
          >
            <button
              tabIndex={0}
              className={st(sharedDepthClasses.accessibilityIcon, {
                isIconShown,
                isOpen,
              })}
              {...accessibilityIconEventListeners}
              aria-label={`Toggle ${label}`}
            >
              <AccessibilityIcon isIconShown={isIconShown} />
            </button>
          </div>
        )}
      </div>

      <MegaMenuContextProvider
        labelRef={labelRef}
        isOpen={isHovered}
        setIsOpen={setIsHovered}
      >
        {children}
      </MegaMenuContextProvider>
    </li>
  );
};
